import axios from 'axios';
import router from '@/router';
import { msalInstance } from '@/composables/msalConfig';
import { refreshAppNow } from '@/utils/refreshApp';
import { tabId } from '@/utils/tabId';
import { currentLocale } from '@/i18n';

axios.defaults.baseURL = process.env.VUE_APP_API_BASE_URL;

// Use Graph or your API scope here:
const MS_SCOPES = ['User.Read']; // or: ['api://<api-app-id>/App.Access']

let msReauthInProgress = false;
let refreshingApp = false;

function redirectToAppLogin(reason = 'ms_session_expired') {
    if (msReauthInProgress) return;
    msReauthInProgress = true;

    // keep info about last method so your /login can show MS button first, etc.
    const loginMethod = localStorage.getItem('loginMethod');
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    if (loginMethod) localStorage.setItem('loginMethod', loginMethod);

    const current = router.currentRoute?.value?.fullPath;
    if (router.currentRoute?.value?.path !== '/login') {
        router.push({ path: '/login', query: { reason, redirect: current } });
    }
}

axios.interceptors.request.use(
    async (config) => {
        // 1) Your API JWT
        const apiToken = localStorage.getItem('token');
        if (apiToken) config.headers.Authorization = `Bearer ${apiToken}`;

        // 2) Microsoft token only for MS users
        if (localStorage.getItem('loginMethod') === 'microsoft') {
            try {
                const account = msalInstance.getActiveAccount() || msalInstance.getAllAccounts()[0];
                if (!account) {
                    // no cached MSAL account for an MS user -> go to your /login
                    redirectToAppLogin('no_msal_account');
                    return Promise.reject(new Error('MS re-auth required (no account)'));
                }

                const res = await msalInstance.acquireTokenSilent({
                    scopes: MS_SCOPES, // NOTE: no "offline_access" here
                    account,
                    // forceRefresh: true, // uncomment if you want a real RT hit each time
                });

                config.headers['X-Microsoft-Token'] = `Bearer ${res.accessToken}`;
            } catch (e) {
                const name = e?.name || '';
                const msg = (e?.errorMessage || e?.message || '').toString();
                if (
                    name === 'InteractionRequiredAuthError' ||
                    msg.includes('AADSTS700082') ||      // RT expired due to inactivity
                    msg.includes('no_tokens_found') ||
                    msg.includes('no_account_in_silent_request')
                ) {
                    // go to your own login page, not MS select-account
                    redirectToAppLogin('ms_token_refresh_failed');
                    return Promise.reject(new Error('MS re-auth required (silent failed)'));
                }
                // Otherwise, proceed without the MS header (if your endpoint doesn’t need it)
                // console.debug('Proceeding without X-Microsoft-Token:', e);
            }
        }

        // 3) App metadata
        config.headers['X-App-Name'] = 'forus-app';
        config.headers['X-App-Version'] = process.env.VUE_APP_VERSION || '0.0.0';

        // Per-tab id for the API log, so two tabs of one login are
        // distinguishable there. Header casing matches X-App-Version because
        // the API looks headers up case-sensitively. See utils/tabId.js.
        config.headers['X-Tab-Id'] = tabId();

        // 4) Locale so the API can localize catalog/display strings (resource
        //    names, server-rendered form labels, etc.)
        config.headers['X-Locale'] = currentLocale();

        return config;
    },
    (error) => Promise.reject(error)
);

axios.interceptors.response.use(
    (response) => response,
    async (error) => {
        const status = error?.response?.status;
        const data = error?.response?.data;

        // --- 401: API session invalid -> go to /login (keep loginMethod) ---
        if (status === 401) {
            const loginMethod = localStorage.getItem('loginMethod');
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            if (loginMethod) localStorage.setItem('loginMethod', loginMethod);

            const current = router.currentRoute?.value?.fullPath;
            if (router.currentRoute?.value?.path !== '/login') {
                router.push({ path: '/login', query: { reason: 'api_unauthorized', redirect: current } });
            }
            return Promise.reject(error);
        }

        // --- 426: App update required ---
        // Accept either status 426 or a semantic code from the API body.
        if (status === 426 || data?.code === 'APP_UPDATE_REQUIRED') {
            if (!refreshingApp) {
                refreshingApp = true;

                // Optional: you could route to /login with a banner instead of auto-refresh.
                // router.push({ path: '/login', query: { reason: 'update_required', min: minHdr || data?.min_version || '' } });

                await refreshAppNow(); // will reload; fallback inside handles non-SW cases too
            }

            // We still reject to end the current request chain; the page reloads anyway.
            return Promise.reject(error);
        }

        return Promise.reject(error);
    }
);

export default axios;
